import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { usePatchApiV1SsoimageIdStatus } from '@/shared/api/generated';
import { useDeleteApiV1SsoimageId } from '@/shared/api/generated/hooks/SSOImageController/useDeleteApiV1SsoimageId';
import { getApiV1SsoimageQueryKey } from '@/shared/api/generated/hooks/SSOImageController/useGetApiV1Ssoimage';

import type { ESSOImageStatus } from '@/shared/api/generated/types/ESSOImageStatus';

export interface UseSlideActionsOptions {
  /** Callback chamado após exclusão bem-sucedida — usado apenas para fechar o modal. */
  readonly onDeleted?: () => void;
}

/**
 * Hook que encapsula as mutations de ações sobre slides da biblioteca.
 * Após cada mutation bem-sucedida, invalida o cache do grid (`useGetApiV1Ssoimage`)
 * para que a UI reflita o novo estado sem sincronização manual.
 *
 * @param options - Callback opcional para fechar o modal após exclusão.
 *
 * @example
 * ```tsx
 * const { changeStatus, confirmDelete, isUpdatingStatus, isDeleting } = useSlideActions({
 *   onDeleted: () => setPendingDeleteId(null),
 * });
 * ```
 */
export function useSlideActions({ onDeleted }: UseSlideActionsOptions = {}) {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { mutate: patchStatus, isPending: isUpdatingStatus } = usePatchApiV1SsoimageIdStatus();

  const { mutate: deleteSlide, isPending: isDeleting } = useDeleteApiV1SsoimageId();

  /**
   * Altera o status de um slide via API.
   * Invalida o cache do grid no sucesso para refetch automático.
   * @param id - ID do SSOImage no backend
   * @param status - Novo status (1 = Draft, 2 = Published)
   */
  const changeStatus = useCallback(
    (id: number, status: ESSOImageStatus) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      patchStatus(
        { id, data: { status } },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: getApiV1SsoimageQueryKey() });
          },
        },
      );
    },
    [patchStatus, queryClient],
  );

  /**
   * Exclui um slide via API (soft delete).
   * Invalida o cache do grid no sucesso e aciona o callback `onDeleted`.
   * @param serverId - ID numérico do backend
   */
  const confirmDelete = useCallback(
    (serverId: number) => {
      deleteSlide(
        { id: serverId },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: getApiV1SsoimageQueryKey() });
            onDeleted?.();
          },
        },
      );
    },
    [deleteSlide, queryClient, onDeleted],
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  return { changeStatus, confirmDelete, isUpdatingStatus, isDeleting } as const;
}
